import { FC, KeyboardEvent, ReactNode, useState } from 'react';
import { Avatar, Button, Col, Divider, Input, Modal, Pagination, Row } from 'antd';
import { TbDotsVertical, TbEye, TbPlayerPause, TbPlayerPlay, TbSearch, TbSettings } from 'react-icons/tb';
import { useDebounce } from 'react-use';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useAlerts, useDeleteAlert, useUpdateAlert } from '@/queries';
import {
    AlertIcon,
    EmptySection,
    VirtualizedList as List,
    MiddleDot,
    PausedAlertIcon,
    RemoveAlertIcon,
    SkeletonList,
    SmallTag,
    WorkspaceResourceActionsDropdown,
    type WorkspaceResourceActionMenuItem,
} from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { AlertCatalog, WorkspaceAlert } from '@/types';
import { ellipsisStyling } from '@/utilities';

import { reportAlertMutationFailure } from '../alertMutationFeedback';

export type AlertManagementSurface = 'preview' | 'properties';

interface AlertManagementListProps {
    action?: ReactNode;
    catalog: AlertCatalog;
    height?: string;
    onDeleted?: (alert: WorkspaceAlert) => void;
    onOpen: (alert: WorkspaceAlert, surface: AlertManagementSurface) => void;
    selectedAlertId?: string;
}

const ALERTS_PAGE_SIZE = 25;

const AlertManagementList: FC<AlertManagementListProps> = ({
    action,
    catalog,
    height = '25rem',
    onDeleted,
    onOpen,
    selectedAlertId,
}) => {
    const { token } = useAntDesignTheme();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [modal, modalContextHolder] = Modal.useModal();
    const updateAlert = useUpdateAlert();
    const deleteAlert = useDeleteAlert();
    const alertsQuery = useAlerts({ limit: ALERTS_PAGE_SIZE, page, search: debouncedSearch });
    const alerts = alertsQuery.data?.items ?? [];
    const mutationsDisabled = updateAlert.isPending || deleteAlert.isPending;

    useDebounce(
        () => {
            setDebouncedSearch(search.trim());
            setPage(1);
        },
        300,
        [search],
    );

    const refreshAfterMutationConflict = () => void alertsQuery.refetch();

    const toggleAlert = (alert: WorkspaceAlert) =>
        void updateAlert
            .mutateAsync({ alertId: alert.id, input: { enabled: !alert.enabled }, revision: alert.revision })
            .then(() => toast.success(alert.enabled ? 'Alert paused.' : 'Alert enabled.'))
            .catch((error: unknown) => {
                reportAlertMutationFailure(error, {
                    fallbackMessage: 'The alert status could not be updated.',
                    operation: 'update',
                });
                refreshAfterMutationConflict();
            });

    const confirmDelete = (alert: WorkspaceAlert) => {
        modal.confirm({
            cancelText: 'Cancel',
            content: 'This permanently stops future evaluation and delivery. Existing audit activity is retained.',
            okButtonProps: { danger: true },
            okText: 'Delete Alert',
            title: `Delete ${alert.name}?`,
            onOk: async () => {
                try {
                    await deleteAlert.mutateAsync({ alertId: alert.id, revision: alert.revision });
                    if (alerts.length === 1 && page > 1) {
                        setPage(page - 1);
                    }
                    toast.success('Alert deleted.');
                    onDeleted?.(alert);
                } catch (error) {
                    reportAlertMutationFailure(error, {
                        fallbackMessage: 'The alert could not be deleted.',
                        operation: 'delete',
                    });
                    refreshAfterMutationConflict();
                    throw error;
                }
            },
        });
    };

    const openFromKeyboard = (event: KeyboardEvent<HTMLElement>, alert: WorkspaceAlert) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(alert, 'properties');
        }
    };

    const renderAlert = (alert: WorkspaceAlert) => {
        const updating = updateAlert.isPending && updateAlert.variables?.alertId === alert.id;
        const deleting = deleteAlert.isPending && deleteAlert.variables?.alertId === alert.id;
        const categoryLabel = catalog.categories.find((entry) => entry.key === alert.category)?.label ?? alert.category;
        const actionItems: WorkspaceResourceActionMenuItem[] = [
            {
                icon: <TbSettings />,
                key: 'properties',
                label: 'Manage Properties',
                onClick: () => onOpen(alert, 'properties'),
                permission: 'update',
                section: 'primary',
            },
            {
                icon: <TbEye />,
                key: 'preview',
                label: 'Preview Alert',
                onClick: () => onOpen(alert, 'preview'),
                permission: 'read',
                section: 'navigation',
            },
            {
                icon: alert.enabled ? <TbPlayerPause /> : <TbPlayerPlay />,
                key: 'enabled',
                label: alert.enabled ? 'Pause Alert' : 'Enable Alert',
                onClick: () => toggleAlert(alert),
                permission: 'update',
                section: 'state',
            },
            {
                danger: true,
                icon: <RemoveAlertIcon />,
                key: 'delete',
                label: 'Delete Alert',
                onClick: () => confirmDelete(alert),
                permission: 'manage',
                section: 'danger',
            },
        ];

        return (
            <List.Item
                key={alert.id}
                role='button'
                tabIndex={0}
                aria-label={`Manage ${alert.name}`}
                extra={
                    <WorkspaceResourceActionsDropdown
                        access={{
                            manage: { disabled: mutationsDisabled },
                            read: { disabled: false },
                            update: { disabled: mutationsDisabled },
                        }}
                        items={actionItems}
                        loading={updating || deleting}
                        menuStyle={{ minWidth: 16 * 15 }}
                        resourceLabel='Alert'
                        stopPropagation
                    >
                        <Button
                            aria-label={`Actions for ${alert.name}`}
                            icon={<TbDotsVertical style={{ color: token.colorTextTertiary }} />}
                            loading={updating || deleting}
                            size='small'
                            type='text'
                        />
                    </WorkspaceResourceActionsDropdown>
                }
                style={{
                    backgroundColor: selectedAlertId === alert.id ? token.colorFillTertiary : undefined,
                    cursor: 'pointer',
                    minHeight: '5rem',
                }}
                onClick={() => onOpen(alert, 'properties')}
                onKeyDown={(event) => openFromKeyboard(event, alert)}
            >
                <List.Item.Meta
                    style={{ alignItems: 'center' }}
                    avatar={
                        <Avatar
                            icon={alert.enabled ? <AlertIcon /> : <PausedAlertIcon />}
                            shape='square'
                            size={48}
                            style={{
                                backgroundColor: token.colorFillQuaternary,
                                border: `1px solid ${token.colorBorder}`,
                                color: token.colorTextTertiary,
                            }}
                        />
                    }
                    title={
                        <span style={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
                            <span style={{ fontWeight: 500, maxWidth: '24rem', minWidth: 0, ...ellipsisStyling }}>
                                {alert.name}
                            </span>
                            <SmallTag
                                accentColor={alert.enabled ? token.colorSuccess : undefined}
                                message={alert.enabled ? 'Active' : 'Paused'}
                            />
                        </span>
                    }
                    description={
                        <span style={{ display: 'block', minWidth: 0, ...ellipsisStyling }}>
                            {alert.description || 'No description'}
                            <MiddleDot horizontalMargin='0.5rem' />
                            {categoryLabel}
                            <MiddleDot horizontalMargin='0.5rem' />
                            {alert.deliveryMethods.length} delivery method
                            {alert.deliveryMethods.length === 1 ? '' : 's'}
                            {alert.nextEvaluationAt ? (
                                <>
                                    <MiddleDot horizontalMargin='0.5rem' />
                                    Next evaluation {new Date(alert.nextEvaluationAt).toLocaleString()}
                                </>
                            ) : null}
                        </span>
                    }
                />
            </List.Item>
        );
    };

    return (
        <>
            {modalContextHolder}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Row gutter={[16, 0]}>
                    <Col flex='auto'>
                        <Input
                            allowClear
                            placeholder='Search Alerts...'
                            prefix={<TbSearch style={{ color: token.colorTextQuaternary, marginRight: '0.325rem' }} />}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </Col>
                    {action ? <Col flex='none'>{action}</Col> : null}
                </Row>
                <Divider style={{ marginBottom: 0, marginTop: '1rem' }} />
                {alertsQuery.isLoading ? (
                    <SkeletonList listLength={6} maxHeight={height} minHeight={height} />
                ) : alertsQuery.isError ? (
                    <EmptySection
                        description='Alerts could not be loaded. Try again or contact support if the problem persists.'
                        extra={
                            <Button size='small' onClick={() => void alertsQuery.refetch()}>
                                Try Again
                            </Button>
                        }
                        height={height}
                        icon={<AlertIcon />}
                        label='Alerts Unavailable'
                    />
                ) : alerts.length === 0 ? (
                    <EmptySection
                        description={
                            search
                                ? 'No alerts match your search.'
                                : 'Create your first alert to monitor a research or diligence signal.'
                        }
                        extra={
                            search ? (
                                <Button size='small' onClick={() => setSearch('')}>
                                    Clear Search
                                </Button>
                            ) : undefined
                        }
                        height={height}
                        icon={<AlertIcon />}
                        label='No Alerts Found'
                    />
                ) : (
                    <>
                        <List
                            dataSource={alerts}
                            overflowFade
                            size='small'
                            style={{ borderBottom: `1px solid ${token.colorBorder}`, height, overflowY: 'auto' }}
                            renderItem={renderAlert}
                        />
                        {(alertsQuery.data?.total ?? 0) > ALERTS_PAGE_SIZE ? (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem' }}>
                                <Pagination
                                    current={page}
                                    pageSize={ALERTS_PAGE_SIZE}
                                    showSizeChanger={false}
                                    total={alertsQuery.data?.total ?? 0}
                                    onChange={setPage}
                                />
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </>
    );
};

export default AlertManagementList;
